
import { useState, useCallback, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { 
  initializeLocalFallback
} from '@/lib/supabase';

export const useDatabaseInitialization = () => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [dbInitAttempted, setDbInitAttempted] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const { toast } = useToast();

  const initializeDatabase = useCallback(async () => {
    if (dbInitAttempted) {
      return;
    }
    
    try {
      setIsLoading(true);
      
      // Lazy load Supabase initialization to avoid unnecessary loading
      const { initializeSupabase, createTablesIfNotExist } = await import('@/lib/supabase');
      
      const connectionTimeout = 8000; // 8 seconds
      const connectionPromise = initializeSupabase();
      const timeoutPromise = new Promise<boolean>((resolve) => {
        setTimeout(() => resolve(false), connectionTimeout);
      });
      
      const supabaseAvailable = await Promise.race([connectionPromise, timeoutPromise]);
      
      if (!supabaseAvailable) {
        await initializeLocalFallback();
        
        setConnectionError("Không thể kết nối đến máy chủ, đang sử dụng dữ liệu cục bộ");
        
        toast({
          title: "Chú ý",
          description: "Không thể kết nối đến máy chủ. Đang sử dụng dữ liệu cục bộ.",
          variant: "destructive"
        });
      } else {
        setConnectionError(null);
        await createTablesIfNotExist();
      }
      
      setDbInitAttempted(true);
      setIsInitialized(true);
      setIsLoading(false);
    } catch (error) {
      await initializeLocalFallback();
      setConnectionError("Lỗi kết nối đến máy chủ: " + (error instanceof Error ? error.message : "Lỗi không xác định"));
      setDbInitAttempted(true);
      setIsInitialized(true);
      setIsLoading(false);
      
      toast({
        title: "Lỗi kết nối",
        description: "Không thể kết nối đến máy chủ. Đang sử dụng dữ liệu cục bộ.",
        variant: "destructive"
      });
    }
  }, [toast, dbInitAttempted]);

  return {
    isInitialized,
    isLoading,
    connectionError,
    dbInitAttempted,
    setConnectionError,
    initializeDatabase
  };
};
